import TimelineIcon from '@mui/icons-material/Timeline';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import { styled as muiStyled, css, lighten } from '@mui/material/styles';
import React from 'react';

import { getDesignTokens } from '~c/components/base/core';
import { CircularProgress } from '~c/components/base/progress';

export interface ButtonProps extends MuiButtonProps {
  className?: string;
  /**
   * If provided, button will be an icon button
   */
  icon?: OverridableComponent<SvgIconTypeMap>;
  /**
   * Outline button (i.e. variant='outlined')
   */
  outlined?: boolean;
  /**
   * Tertiary button (i.e. color='secondary')
   */
  tertiary?: boolean;
  /**
   * Render an indeterminate loading indicator and disabled the button
   */
  loading?: boolean;

  component?: React.ElementType;
}

const ButtonImpl = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      icon,
      outlined,
      tertiary,
      color: _color = 'primary',
      variant = 'contained',
      loading,
      children,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const Icon = icon;
    const color = tertiary ? 'secondary' : _color;
    const iconColor =
      color === 'primary'
        ? theme.palette.primary.main
        : theme.palette.text.primary;
    const iconStyles = {
      fill: iconColor,
      color: iconColor,
      ...(Icon === TimelineIcon && { transform: 'scaleY(-1)' }),
    };
    return (
      <div className={className}>
        {Icon ? (
          <IconButton ref={ref} className="v2-icon-button" {...props}>
            <Icon style={iconStyles} />
          </IconButton>
        ) : (
          <MuiButton
            ref={ref}
            variant={variant}
            color={color}
            {...props}
            {...(outlined && { variant: 'outlined' })}
            {...(loading && {
              variant: 'outlined',
              disabled: true,
              startIcon: <CircularProgress size={16} color={color} />,
            })}
          >
            {children}
          </MuiButton>
        )}
      </div>
    );
  }
);

/**
 * Customized Mui Button component.
 *
 * Default is a contained button.
 * If an icon is provided, it will be an icon button.
 *
 * It inherits all properties of a Button
 */
export const Button = muiStyled(ButtonImpl)(({
  theme,
  color,
  tertiary,
  outlined,
  variant,
}) => {
  let _color = !color || color === 'inherit' ? 'primary' : color;
  if (tertiary) {
    _color = 'secondary';
  }
  const mainColor = theme.palette[_color].main;
  const lightColor = lighten(mainColor, 0.7);
  const { disabledOpacity } = theme.palette.action;
  const isOutlined = !!outlined || variant === 'outlined';
  const isDarkMode_ = theme.palette.mode === 'dark';
  const t = getDesignTokens(isDarkMode_ ? 'dark' : 'light');
  // Muted-but-solid fill for disabled buttons (keeps the label readable)
  const disabledFill = isDarkMode_
    ? 'rgba(255,255,255,0.10)'
    : 'rgba(15,23,42,0.06)';

  return css`
    .v2-icon-button {
      color: ${mainColor};
      border: ${mainColor} solid ${isOutlined ? 1 : 0}px;

      :hover {
        color: ${lightColor};
        border: ${lightColor} solid ${isOutlined ? 1 : 0}px;
      }
      :disabled {
        opacity: ${disabledOpacity};
      }
    }

    /*
     * Disabled styling: instead of fading the whole button with a low opacity
     * (which also fades the text below readability), give the disabled state a
     * muted-but-solid fill and a legible muted text colour. The button clearly
     * reads as inactive while the label stays readable.
     */
    .MuiButton-root.MuiButton-text {
      :disabled {
        color: ${t.textSecondary};
        opacity: 1;
      }
    }
    .MuiButton-root.MuiButton-contained {
      :disabled {
        background-color: ${disabledFill};
        color: ${t.textSecondary};
        box-shadow: none;
        opacity: 1;
      }
      /* Keep solid background on hover (no transparency) — only when enabled */
      &:hover:not(:disabled) {
        background-color: ${t.buttonPrimaryHover} !important;
      }
    }
    .MuiButton-root.MuiButton-outlined {
      :enabled {
        color: ${isDarkMode_ ? t.accent : t.link};
        border-color: ${isDarkMode_ ? t.accent : t.link};
      }
      :disabled {
        border: ${t.borderDefault} solid 1px;
        color: ${t.textSecondary};
        opacity: 1;
      }
      &:hover:not(:disabled) {
        background-color: ${t.surface2} !important;
      }
    }

    /* Increase height of button for size=large */
    .MuiButton-root.MuiButton-sizeLarge {
      height: 56px;
    }
  `;
});

export default Button;
